<template>
  <el-dialog
    v-model="visible"
    title="添加子渠道"
    width="620px"
    align-center
    destroy-on-close
    class="add-sub-channel-dialog"
    @closed="onDialogClosed"
  >
    <el-form
      ref="formRef"
      :model="form"
      :rules="rules"
      label-position="top"
      class="add-sub-channel-form"
    >
      <div class="enterprise-query-section">
        <div class="enterprise-query-section__head">
          <span class="enterprise-query-section__title">企业查询</span>
          <span class="enterprise-query-section__desc">输入企业名称查找已实名认证的普通企业，选定后再填写下方信息</span>
        </div>
        <div class="enterprise-query-row">
          <el-input
            v-model="form.queryEntName"
            placeholder="请输入企业名称"
            clearable
            size="large"
            class="enterprise-query-input"
            @keyup.enter="handleSearchEnterprise"
          />
          <el-button
            type="primary"
            size="large"
            class="sub-channel-query-btn"
            :loading="enterpriseQueryLoading"
            @click="handleSearchEnterprise"
          >
            查询
          </el-button>
        </div>
      </div>
      <div class="add-sub-channel-form-fields">
        <el-form-item
          label="企业名称"
          prop="linkedEnterpriseName"
        >
          <el-input
            :model-value="form.linkedEnterpriseName"
            disabled
            placeholder="请先查询匹配企业"
            class="add-form-control sub-channel-name-input"
            readonly
          />
        </el-form-item>
        <el-form-item
          v-if="form.linkedEnterpriseId"
          label="联系电话"
        >
          <el-input
            :model-value="form.linkedLinkmanPhone"
            readonly
            disabled
            placeholder="—"
            class="add-form-control sub-channel-name-input"
          />
        </el-form-item>
        <el-form-item
          v-if="form.linkedEnterpriseId"
          label="验证码"
          prop="verificationSmsCode"
        >
          <div class="sms-code-row">
            <el-input
              v-model="form.verificationSmsCode"
              placeholder="请输入验证码"
              clearable
              class="sms-code-input"
            />
            <el-button
              type="primary"
              class="sms-code-send-btn"
              :disabled="smsCodeButtonDisabled"
              @click="handleGetVerificationCode"
            >
              {{ smsCodeButtonText }}
            </el-button>
          </div>
        </el-form-item>
      
        <el-form-item
          label="子渠道名称"
          prop="channelOrgName"
        >
          <el-input
            v-model="form.channelOrgName"
            clearable
            maxlength="20"
            show-word-limit
            placeholder="请输入子渠道名称"
            class="add-form-control"
          />
        </el-form-item>
        <el-form-item
          label="渠道类型"
          prop="channelIdentityType"
        >
          <el-select
            v-model="form.channelIdentityType"
            placeholder="请选择渠道类型"
            clearable
            class="add-form-control"
            @change="handleChannelIdentityTypeChange"
          >
            <el-option
              v-for="opt in identityOptionsForSelect"
              :key="opt.value"
              :label="opt.label"
              :value="opt.value"
            />
          </el-select>
        </el-form-item>
        <el-form-item
          v-if="isDistrictAssocSelected"
          label="所属区级文联"
          prop="channelDistrictType"
        >
          <el-select
            v-model="form.channelDistrictType"
            placeholder="请选择所属区级文联"
            clearable
            class="add-form-control"
          >
            <el-option
              v-for="opt in channelDistrictTypeOptions"
              :key="opt.value"
              :label="opt.label"
              :value="opt.value"
            />
          </el-select>
        </el-form-item>
        <el-form-item
          label="备注信息"
          prop="remark"
        >
          <el-input
            v-model="form.remark"
            type="textarea"
            :rows="5"
            placeholder="请输入备注信息"
            resize="none"
            class="add-form-control add-form-textarea"
          />
        </el-form-item>
      </div>
    </el-form>
    <template #footer>
      <div class="add-sub-channel-footer">
        <el-button @click="visible = false">
          取消
        </el-button>
        <el-button
          type="primary"
          class="add-sub-channel-submit-btn"
          :loading="submitLoading"
          @click="handleSubmit"
        >
          确定
        </el-button>
      </div>
    </template>
  </el-dialog>

  <el-dialog
    v-model="detailDialogVisible"
    title="查询到的企业信息"
    width="520px"
    append-to-body
    align-center
    destroy-on-close
    class="enterprise-detail-dialog"
    @closed="onDetailDialogClosed"
  >
    <el-descriptions
      v-if="pendingEnterprise"
      :column="1"
      border
      class="enterprise-detail-descriptions"
    >
      <el-descriptions-item label="企业名称">
        {{ pendingEnterprise.entName || '—' }}
      </el-descriptions-item>
      <el-descriptions-item label="统一社会信用代码">
        {{ pendingEnterprise.creditCode || '—' }}
      </el-descriptions-item>
      <el-descriptions-item label="联系人">
        {{ pendingEnterprise.linkmanName || '—' }}
      </el-descriptions-item>
      <el-descriptions-item label="联系电话">
        {{ pendingEnterprise.linkmanPhone || '—' }}
      </el-descriptions-item>
    </el-descriptions>
    <template #footer>
      <div class="enterprise-detail-footer">
        <el-button @click="handleDetailCancel">
          取消
        </el-button>
        <el-button
          type="primary"
          @click="handleDetailConfirm"
        >
          确认选用
        </el-button>
      </div>
    </template>
  </el-dialog>
</template>

<script>
import { ElMessage } from 'element-plus'
import {
  authorizeGovernanceSubChannelUnderCurrentParent,
  getVerifiedNormalEnterpriseForSubChannel
} from '@/api/enterprise'
import { showEnhancedAlertDialog } from '@/utils/messageUtils'
import { getSmsCaptcha } from '@/api/utils/captcha'
import { GOVERNANCE_CHANNEL_IDENTITY_PRIMARY, GOVERNANCE_CHANNEL_IDENTITY_DISTRICT_ASSOC } from '@/enums/ChannelTypeEnum'
import { GLOBAL_PHONE_PATTERN } from '@/utils/idValidator'

const emptyForm = () => ({
  queryEntName: '',
  channelIdentityType: '',
  channelDistrictType: '',
  linkedEnterpriseName: '',
  linkedEnterpriseId: null,
  linkedLinkmanPhone: '',
  channelOrgName: '',
  verificationSmsCode: '',
  remark: ''
})

export default {
  name: 'AddSubChannelDialog',
  props: {
    modelValue: {
      type: Boolean,
      default: false
    },
    /** 渠道身份类型选项（不含「全部」），与 governance_channel_identity_type 一致 */
    identityOptions: {
      type: Array,
      default: () => []
    },
    /** 区级渠道地区类型选项，与 channel_district_type 一致 */
    channelDistrictTypeOptions: {
      type: Array,
      default: () => []
    }
  },
  emits: ['update:modelValue', 'success'],
  data() {
    return {
      form: emptyForm(),
      enterpriseQueryLoading: false,
      detailDialogVisible: false,
      pendingEnterprise: null,
      smsCodeButtonText: '获取验证码',
      smsCodeButtonDisabled: false,
      smsCountdownTimer: null,
      submitLoading: false,
      rules: {
        channelIdentityType: [
          { required: true, message: '渠道类型不能为空', trigger: 'change' }
        ],
        channelDistrictType: [
          {
            validator: (_, val, callback) => {
              if (!this.isDistrictAssocSelected) {
                callback()
                return
              }
              if (!(val || '').trim()) {
                callback(new Error('请选择所属区级文联'))
                return
              }
              callback()
            },
            trigger: 'change'
          }
        ],
        linkedEnterpriseName: [
          { required: true, message: '企业不能为空', trigger: 'change' }
        ],
        channelOrgName: [
          {
            required: true,
            validator: (_, val, callback) => {
              if (!(val || '').trim()) {
                callback(new Error('请输入子渠道名称'))
                return
              }
              callback()
            },
            trigger: ['blur', 'change']
          }
        ],
        verificationSmsCode: [
          {
            required: true,
            validator: (_, val, callback) => {
              if (!this.form.linkedEnterpriseId) {
                callback()
                return
              }
              if (!(val || '').trim()) {
                callback(new Error('请输入验证码'))
                return
              }
              callback()
            },
            trigger: ['blur', 'change']
          }
        ]
      }
    }
  },
  computed: {
    visible: {
      get() {
        return this.modelValue
      },
      set(val) {
        this.$emit('update:modelValue', val)
      }
    },
    /** 与子渠道场景一致：不展示专项治理「一级渠道」（EhannelChannelIdentityTypeEnum.PRIMARY） */
    identityOptionsForSelect() {
      const primary = GOVERNANCE_CHANNEL_IDENTITY_PRIMARY
      return (this.identityOptions || []).filter(
        opt => String(opt.value ?? '').trim() !== primary
      )
    },
    isDistrictAssocSelected() {
      return String(this.form.channelIdentityType ?? '').trim() === GOVERNANCE_CHANNEL_IDENTITY_DISTRICT_ASSOC
    }
  },
  watch: {
    modelValue(val) {
      if (val) {
        this.resetForm()
        this.detailDialogVisible = false
        this.pendingEnterprise = null
        this.$nextTick(() => {
          this.$refs.formRef?.clearValidate()
        })
      } else {
        this.detailDialogVisible = false
      }
    }
  },
  beforeUnmount() {
    this.clearSmsCountdown()
  },
  methods: {
    clearSmsCountdown() {
      if (this.smsCountdownTimer != null) {
        clearInterval(this.smsCountdownTimer)
        this.smsCountdownTimer = null
      }
      this.smsCodeButtonText = '获取验证码'
      this.smsCodeButtonDisabled = false
    },
    resetForm() {
      this.clearSmsCountdown()
      this.form = emptyForm()
    },
    handleChannelIdentityTypeChange() {
      if (!this.isDistrictAssocSelected) {
        this.form.channelDistrictType = ''
      }
      this.$nextTick(() => {
        this.$refs.formRef?.clearValidate('channelDistrictType')
      })
    },
    onDialogClosed() {
      this.detailDialogVisible = false
      this.pendingEnterprise = null
      this.resetForm()
      this.$refs.formRef?.clearValidate()
    },
    onDetailDialogClosed() {
      this.pendingEnterprise = null
    },
    handleDetailCancel() {
      this.detailDialogVisible = false
    },
    handleDetailConfirm() {
      const row = this.pendingEnterprise
      if (!row || row.id == null) {
        this.detailDialogVisible = false
        return
      }
      this.form.linkedEnterpriseId = row.id
      this.form.linkedEnterpriseName = row.entName || ''
      this.form.channelOrgName = ''
      this.form.linkedLinkmanPhone = (row.linkmanPhone || '').trim()
      this.form.verificationSmsCode = ''
      this.clearSmsCountdown()
      this.detailDialogVisible = false
      this.pendingEnterprise = null
      this.$nextTick(() => {
        this.$refs.formRef?.validateField('linkedEnterpriseName')
      })
      ElMessage.success('已选用该企业')
    },
    async handleSearchEnterprise() {
      const keyword = (this.form.queryEntName || '').trim()
      if (!keyword) {
        ElMessage.warning('请输入企业名称')
        return
      }
      this.form.linkedEnterpriseId = null
      this.form.linkedEnterpriseName = ''
      this.form.channelOrgName = ''
      this.form.linkedLinkmanPhone = ''
      this.form.verificationSmsCode = ''
      this.clearSmsCountdown()
      this.enterpriseQueryLoading = true
      try {
        const res = await getVerifiedNormalEnterpriseForSubChannel({
          entName: keyword
        })
        if (res && res.code === 200) {
          const row = res.data
          if (row && row.id != null) {
            this.pendingEnterprise = { ...row }
            this.detailDialogVisible = true
          } else {
            await showEnhancedAlertDialog('未匹配到企业')
          }
        }
      } catch (e) {
        console.error('查询企业失败:', e)
      } finally {
        this.enterpriseQueryLoading = false
      }
    },
    async handleGetVerificationCode() {
      const phone = (this.form.linkedLinkmanPhone || '').trim()
      if (!phone) {
        ElMessage.warning('请先选用企业以获取联系电话')
        return
      }
      if (!GLOBAL_PHONE_PATTERN.test(phone)) {
        ElMessage.error('子渠道联系电话格式不正确，无法发送验证码')
        return
      }
      try {
        const response = await getSmsCaptcha(phone)
        if (response && response.code === 200) {
          ElMessage.success('验证码已发送')
          this.startSmsCountdown()
        }
      } catch (error) {
        console.error('获取验证码失败:', error)
        ElMessage.error(error.message || '获取验证码失败，请重试')
      }
    },
    startSmsCountdown() {
      this.clearSmsCountdown()
      let left = 60
      this.smsCodeButtonDisabled = true
      this.smsCodeButtonText = `${left}秒后重新获取`
      this.smsCountdownTimer = setInterval(() => {
        left -= 1
        if (left <= 0) {
          this.clearSmsCountdown()
          return
        }
        this.smsCodeButtonText = `${left}秒后重新获取`
      }, 1000)
    },
    handleSubmit() {
      this.$refs.formRef?.validate(async valid => {
        if (!valid) return
        const id = this.form.linkedEnterpriseId
        if (id == null) {
          ElMessage.warning('请先查询并选用企业')
          return
        }
        const phone = (this.form.linkedLinkmanPhone || '').trim()
        const code = (this.form.verificationSmsCode || '').trim()
        if (!phone || !code) {
          ElMessage.warning('请填写联系人手机号对应的短信验证码')
          return
        }
        this.submitLoading = true
        try {
          const remark = (this.form.remark || '').trim()
          const orgName = (this.form.channelOrgName || '').trim()
          const res = await authorizeGovernanceSubChannelUnderCurrentParent({
            id,
            channelIdentityType: this.form.channelIdentityType,
            channelDistrictType: this.isDistrictAssocSelected
              ? (this.form.channelDistrictType || '').trim()
              : undefined,
            channelOrgName: orgName,
            channelOrgRemarks: remark || undefined,
            linkmanPhone: phone,
            verificationSmsCode: code
          })
          if (res && res.code === 200) {
            ElMessage.success(res.msg || '添加成功')
            this.visible = false
            this.$emit('success')
          }
        } catch (e) {
          console.error('添加子渠道失败:', e)
        } finally {
          this.submitLoading = false
        }
      })
    }
  }
}
</script>

<style lang="scss">
.add-sub-channel-dialog.el-dialog {
  border-radius: 8px;
  padding: 0;

  .el-dialog__header {
    margin: 0;
    padding: 20px 24px 16px;
    border-bottom: 1px solid #ebeef5;
  }

  .el-dialog__title {
    font-size: 18px;
    font-weight: 600;
    color: #303133;
  }

  .el-dialog__headerbtn {
    top: 18px;
    right: 18px;
  }

  .el-dialog__body {
    padding: 24px 24px 8px;
  }

  .el-dialog__footer {
    padding: 16px 24px 24px;
    border-top: none;
  }
}

.add-sub-channel-form {
  .add-sub-channel-form-fields {
    padding-left: 18px;
    padding-right: 18px;
    box-sizing: border-box;
  }

  .el-form-item {
    margin-bottom: 22px;
  }

  &.el-form--label-top .el-form-item__label {
    justify-content: flex-start;
    margin-bottom: 8px;
    padding: 0;
    height: auto;
    line-height: 1.4;
    font-weight: 600;
    font-size: 14px;
    color: #303133;
  }

  .add-form-control {
    width: 100%;
  }

  .add-form-textarea .el-textarea__inner {
    min-height: 120px !important;
    border-radius: 8px;
    padding: 10px 12px;
    line-height: 1.5;
  }

  .enterprise-query-section {
    margin: -4px 0 24px;
    padding: 18px 18px 20px;
    background: linear-gradient(180deg, #f0f5ff 0%, #f7f9fc 100%);
    border: 1px solid #e4eafd;
    border-radius: 10px;
    box-sizing: border-box;
  }

  .enterprise-query-section__head {
    margin-bottom: 14px;
  }

  .enterprise-query-section__title {
    display: block;
    font-size: 16px;
    font-weight: 600;
    color: #303133;
    letter-spacing: 0.02em;
    margin-bottom: 6px;
  }

  .enterprise-query-section__desc {
    display: block;
    font-size: 13px;
    line-height: 1.5;
    color: #909399;
  }

  .enterprise-query-row {
    display: flex;
    width: 100%;
    align-items: stretch;
    gap: 14px;
  }

  .enterprise-query-input {
    flex: 1;
    min-width: 0;

    .el-input__wrapper {
      border-radius: 10px;
      min-height: 48px;
      padding-left: 14px;
      padding-right: 14px;
      box-sizing: border-box;
      box-shadow: 0 1px 2px rgb(13 98 243 / 6%);
    }

    .el-input__inner {
      font-size: 15px;
    }
  }

  .sub-channel-name-input .el-input__wrapper {
    border-radius: 8px;
    min-height: 40px;
    background-color: #f5f7fa;
    box-shadow: none;
    cursor: default;
  }

  .sms-code-row {
    display: flex;
    width: 100%;
    align-items: center;
    gap: 12px;
  }

  .sms-code-input {
    flex: 1;
    min-width: 0;

    .el-input__wrapper {
      border-radius: 8px;
      min-height: 40px;
      height: 40px;
      box-sizing: border-box;
    }
  }

  .sms-code-send-btn {
    flex-shrink: 0;
    height: 40px;
    min-height: 40px;
    padding: 0 18px;
    border-radius: 8px;
    font-weight: 500;
    background-color: #0d62f3;
    border-color: #0d62f3;

    &:hover:not(:disabled) {
      background-color: #3d7ef5;
      border-color: #3d7ef5;
    }
  }

  .sub-channel-query-btn {
    flex-shrink: 0;
    min-height: 48px;
    padding: 0 28px;
    box-sizing: border-box;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 10px;
    font-size: 15px;
    font-weight: 600;
    background-color: #0d62f3;
    border-color: #0d62f3;

    &:hover {
      background-color: #3d7ef5;
      border-color: #3d7ef5;
    }
  }

  .el-select .el-input__wrapper {
    min-height: 40px;
    border-radius: 8px;
  }
}

.add-sub-channel-footer {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 16px;
}

.add-sub-channel-submit-btn {
  min-width: 88px;
  background-color: #0d62f3;
  border-color: #0d62f3;

  &:hover {
    background-color: #3d7ef5;
    border-color: #3d7ef5;
  }
}

.enterprise-detail-dialog.el-dialog {
  border-radius: 8px;
  padding: 0;

  .el-dialog__header {
    margin: 0;
    padding: 16px 20px 12px;
    border-bottom: 1px solid #ebeef5;
  }

  .el-dialog__title {
    font-size: 16px;
    font-weight: 600;
    color: #303133;
  }

  .el-dialog__body {
    padding: 20px 20px 8px;
  }

  .el-dialog__footer {
    padding: 12px 20px 20px;
    border-top: none;
  }
}

.enterprise-detail-descriptions {
  :deep(.el-descriptions__label) {
    font-weight: 500;
    color: #606266;
    width: 140px;
  }

  :deep(.el-descriptions__content) {
    color: #303133;
  }
}

.enterprise-detail-footer {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 12px;
}

@media (max-width: 768px) {
  .add-sub-channel-dialog.el-dialog {
    width: 100vw !important;
    max-width: 100vw;
    margin: 0 !important;
    height: 100vh;
    max-height: 100vh;
    border-radius: 0;
    display: flex;
    flex-direction: column;

    .el-dialog__header {
      padding: 16px 44px 14px 16px;
      flex-shrink: 0;
    }

    .el-dialog__title {
      font-size: 16px;
    }

    .el-dialog__headerbtn {
      top: 16px;
      right: 12px;
    }

    .el-dialog__body {
      flex: 1;
      min-height: 0;
      padding: 16px 16px 8px;
      overflow-y: auto;
      overscroll-behavior: contain;
      -webkit-overflow-scrolling: touch;
    }

    .el-dialog__footer {
      padding: 12px 16px calc(12px + env(safe-area-inset-bottom, 0px));
      flex-shrink: 0;
    }
  }

  .add-sub-channel-form {
    .add-sub-channel-form-fields {
      padding-left: 0;
      padding-right: 0;
    }

    .el-form-item {
      margin-bottom: 18px;
    }

    .enterprise-query-section {
      margin-top: 0;
      margin-bottom: 20px;
      padding: 14px 14px 16px;
    }

    .enterprise-query-section__title {
      font-size: 15px;
    }

    .enterprise-query-row {
      flex-direction: column;
      gap: 10px;
    }

    .sub-channel-query-btn {
      width: 100%;
      min-height: 44px;
      padding: 0 16px;
    }

    .sms-code-row {
      flex-direction: column;
      align-items: stretch;
      gap: 10px;
    }

    .sms-code-send-btn {
      width: 100%;
      height: 40px;
    }
  }

  .add-sub-channel-footer {
    flex-direction: column;
    align-items: stretch;
    width: 100%;
    gap: 10px;

    .el-button {
      width: 100%;
      margin-left: 0 !important;
    }
  }

  .enterprise-detail-dialog.el-dialog {
    width: calc(100vw - 32px) !important;
    max-width: calc(100vw - 32px);
    margin-top: 8vh !important;

    .el-dialog__body {
      padding: 16px;
      max-height: calc(84vh - 120px);
      overflow-y: auto;
    }
  }

  .enterprise-detail-descriptions {
    :deep(.el-descriptions__label) {
      width: 112px;
    }
  }

  .enterprise-detail-footer {
    flex-direction: column;
    align-items: stretch;
    width: 100%;
    gap: 10px;

    .el-button {
      width: 100%;
      margin-left: 0 !important;
    }
  }
}

@media (max-width: 480px) {
  .add-sub-channel-dialog.el-dialog {
    .el-dialog__header {
      padding: 14px 40px 12px 12px;
    }

    .el-dialog__body {
      padding: 12px 12px 8px;
    }

    .el-dialog__footer {
      padding: 10px 12px calc(10px + env(safe-area-inset-bottom, 0px));
    }
  }

  .add-sub-channel-form {
    .enterprise-query-section {
      padding: 12px;
    }

    .enterprise-query-section__desc {
      font-size: 12px;
    }
  }

  .enterprise-detail-dialog.el-dialog {
    width: calc(100vw - 24px) !important;
    max-width: calc(100vw - 24px);
  }

  .enterprise-detail-descriptions {
    :deep(.el-descriptions__label) {
      width: 96px;
      font-size: 13px;
    }

    :deep(.el-descriptions__content) {
      font-size: 13px;
    }
  }
}
</style>
